import { AlertTriangle, Bot } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useAlfPerformance } from '../../hooks/useFaqMonitor.js';
import {
  ALF_DEFAULT_MIN_CITED,
  ALF_DEFAULT_PERIOD_DAYS,
  computeAlfPeriod,
  includesUnrecordedDays,
  summarizeAlfPerformance,
} from '../../lib/alf-performance.js';
import type { AlfPeriodPreset } from '../../lib/alf-performance.js';
import type { FaqAlfPerformanceQuery } from '../../types/faq-monitor.js';
import {
  AlfControls,
  AlfExcludedNote,
  AlfOverviewCards,
  AlfPerformanceRow,
} from './AlfPerformance.js';

/**
 * 選んだ期間を API に渡すクエリへ変換する。
 *
 * 任意期間は両端が埋まるまで送らない。片側だけで問い合わせると、API 側の既定
 * （前日までの直近28日）と混ざった期間が返り、画面の表示と中身が食い違う。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 選んだ期間と引用回数の下限を、ALF実績APIのクエリに変換する
 * @graph-connects computeAlfPeriod [calls] 前日までの直近N日の算出
 */
export function toAlfQuery(
  preset: AlfPeriodPreset,
  customFrom: string,
  customTo: string,
  minCited: number,
  now: Date = new Date(),
): FaqAlfPerformanceQuery {
  if (preset === 'custom') {
    if (customFrom === '' || customTo === '') return { minCited };
    return { from: customFrom, to: customTo, minCited };
  }
  const { from, to } = computeAlfPeriod(preset, now);
  return { from, to, minCited };
}

/**
 * 画面に常に出しておく但し書き。
 *
 * どれも「数字をそのまま信じると判断を誤る」点で、畳むと読まれない。
 * 折りたたみにせず、表の直下に置いたままにする。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 完結率の読み方（記録の開始日・公式値との差・低い理由が2種類あること）を常時示す
 * @graph-connects none
 */
export function AlfFootnotes({
  periodFrom,
  dataAvailableFrom,
}: {
  periodFrom: string;
  dataAvailableFrom: string | null;
}) {
  const { t } = useTranslation();
  const unrecorded = includesUnrecordedDays(periodFrom, dataAvailableFrom);

  return (
    <div className="space-y-2 text-xs text-foreground-secondary">
      {unrecorded && dataAvailableFrom !== null && (
        <p
          className="flex items-start gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-400"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t('faqMonitor.alfNoteUnrecorded', { date: dataAvailableFrom })}</span>
        </p>
      )}
      <p>{t('faqMonitor.alfNoteOfficialGap')}</p>
      <p>{t('faqMonitor.alfNoteJudgement')}</p>
      <p>{t('faqMonitor.alfNoteWontFix')}</p>
    </div>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business ALFが引用したFAQ記事ごとの実績。完結率の低い順に並べ、引用元の問い合わせを開いて中身を確かめたうえで対応方針を決められるようにする。
 * @graph-connects useAlfPerformance [calls] ALF実績の取得
 * @graph-connects toAlfQuery [calls] 期間と下限のクエリ変換
 * @graph-connects summarizeAlfPerformance [calls] 一覧全体の要約
 * @graph-connects AlfOverviewCards [calls] 要約の数字カード
 * @graph-connects AlfControls [calls] 期間と引用回数の下限の操作
 * @graph-connects AlfPerformanceRow [calls] 記事1本ぶんの行
 * @graph-connects AlfExcludedNote [calls] 一覧から外れたぶんの内訳
 * @graph-connects AlfFootnotes [calls] 完結率の読み方の但し書き
 */
export function AlfPerformanceSection() {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<AlfPeriodPreset>(ALF_DEFAULT_PERIOD_DAYS);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [minCited, setMinCited] = useState<number>(ALF_DEFAULT_MIN_CITED);
  const [openHash, setOpenHash] = useState<string | null>(null);

  // 日付は選択が変わったときだけ計算し直す。開きっぱなしで日付をまたぐと期間は
  // 前日のままだが、集計対象は前日までなので中身は変わらない。毎レンダーで
  // 計算し直すと queryKey が動いて取得が走り続ける。
  const query = useMemo(
    () => toAlfQuery(preset, customFrom, customTo, minCited),
    [preset, customFrom, customTo, minCited],
  );
  const { data, isLoading, error } = useAlfPerformance(query);

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center gap-2">
        <Bot className="h-5 w-5 text-foreground-muted" aria-hidden="true" />
        <h2 className="font-semibold text-foreground">{t('faqMonitor.alfHeading')}</h2>
      </div>
      <p className="text-sm text-foreground-secondary">{t('faqMonitor.alfDescription')}</p>

      {isLoading && (
        <p className="py-4 text-sm text-foreground-secondary">{t('faqMonitor.loading')}</p>
      )}

      {!isLoading && (error !== null || data === undefined) && (
        <p className="py-4 text-sm text-red-600 dark:text-red-400" role="alert">
          {t('faqMonitor.alfLoadError')}
        </p>
      )}

      {!isLoading && error === null && data !== undefined && (
        <>
          <AlfControls
            preset={preset}
            onPresetChange={setPreset}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
            minCited={minCited}
            onMinCitedChange={setMinCited}
            periodFrom={data.periodFrom}
            periodTo={data.periodTo}
          />
          <AlfOverviewCards overview={summarizeAlfPerformance(data)} minCited={minCited} />

          {data.items.length === 0 ? (
            <p className="py-4 text-sm text-foreground-secondary">{t('faqMonitor.alfEmpty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[48rem] text-sm">
                <thead>
                  <tr className="text-left text-xs text-foreground-muted">
                    <th className="px-2 py-1 font-medium">{t('faqMonitor.alfColArticle')}</th>
                    <th className="px-2 py-1 text-right font-medium">
                      {t('faqMonitor.alfColCited')}
                    </th>
                    <th className="px-2 py-1 text-right font-medium">
                      {t('faqMonitor.alfColResolved')}
                    </th>
                    <th className="px-2 py-1 font-medium">{t('faqMonitor.alfColRate')}</th>
                    <th className="px-2 py-1 font-medium">{t('faqMonitor.alfColDiagnostic')}</th>
                    <th className="px-2 py-1 font-medium">{t('faqMonitor.alfColDisposition')}</th>
                    <th className="px-2 py-1" />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <AlfPerformanceRow
                      key={item.dispositionHash}
                      item={item}
                      open={openHash === item.dispositionHash}
                      onToggle={() => {
                        setOpenHash(
                          openHash === item.dispositionHash ? null : item.dispositionHash,
                        );
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <AlfExcludedNote data={data} minCited={minCited} />
          <AlfFootnotes periodFrom={data.periodFrom} dataAvailableFrom={data.dataAvailableFrom} />
        </>
      )}
    </Card>
  );
}
